<template>
  <div class="app-container">
    <!-- 个人信息卡片 -->
    <el-card class="profile-card" shadow="never">
      <!-- 头像 & 基础信息 -->
      <div class="profile-header">
        <div class="avatar-box">
          <el-avatar :size="72" :src="userInfo.avatar" />
        </div>
        <div class="profile-meta">
          <div class="profile-name">{{ userInfo.nick || '--' }}</div>
          <div class="profile-tags">
            <span class="meta-tag">ID: {{ userInfo.id }}</span>
            <span class="meta-tag">
              邀请码: {{ userInfo.uid }}
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(userInfo.uid, '邀请码')" />
            </span>
          </div>
        </div>
      </div>

      <!-- 邀请链接 & 二维码 -->
      <div class="invite-area">
        <div class="invite-link-box">
          <div class="invite-label">邀请链接</div>
          <div class="invite-link-row">
            <el-input :value="userInfo.url" readonly size="small" class="link-input" />
            <el-button type="primary" size="small" icon="el-icon-document-copy" @click="copyText(userInfo.url, '邀请链接')">复制</el-button>
          </div>
        </div>
        <div class="qrcode-box">
          <div class="qrcode-img" v-if="qrCodeDataUrl">
            <img :src="qrCodeDataUrl" alt="邀请二维码" />
          </div>
          <div v-else class="qrcode-img qrcode-loading">生成中...</div>
          <el-button type="text" size="mini" icon="el-icon-download" @click="downloadQrCode">下载</el-button>
        </div>
      </div>
    </el-card>

    <!-- 积分卡片 -->
    <el-card class="points-card" shadow="never">
      <div class="points-header">
        <h3>可提现积分</h3>
        <el-button type="text" icon="el-icon-refresh" @click="refreshPoints">刷新</el-button>
      </div>
      <div class="points-grid">
        <div class="points-item">
          <div class="points-num primary">{{ userInfo.integral || 0 }}</div>
          <div class="points-desc">积分</div>
        </div>
      </div>
    </el-card>

    <!-- 提现弹窗 -->
    <el-dialog title="积分提现" :visible.sync="withdrawDialogVisible" width="460px" :before-close="handleCloseWithdrawDialog">
      <div class="withdraw-info">
        <div class="info-item">
          <span>剩余待提现积分：</span>
          <span class="info-value points">{{ userInfo.integral || 0 }}</span>
        </div>
        <div class="info-item">
          <span>提现比例：</span>
          <span class="info-value">10积分 = ¥1.00</span>
        </div>
      </div>
      <el-form ref="withdrawForm" :model="withdrawForm" :rules="withdrawRules" label-width="90px">
        <el-form-item label="提现积分" prop="points">
          <el-input v-model="withdrawForm.points" placeholder="请输入提现积分数量" type="number" @input="calculateAmount">
            <template slot="append">积分</template>
          </el-input>
        </el-form-item>
        <el-form-item label="提现金额">
          <el-input v-model="withdrawForm.amount" placeholder="0.00" readonly>
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="withdrawDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="withdrawLoading" @click="submitWithdraw">确认提现</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCurrUserInfo, postAction } from '@/api/user'
import QRCode from 'qrcode'

export default {
  data() {
    return {
      userInfo: {},
      qrCodeDataUrl: '',
      withdrawDialogVisible: false,
      withdrawLoading: false,
      withdrawForm: { points: '', amount: '0.00' },
      withdrawRules: {
        points: [
          { required: true, message: '请输入提现积分数量', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const points = parseInt(value)
              if (!points || points <= 0) return callback(new Error('提现积分必须大于0'))
              if (points > (this.userInfo.integral || 0)) return callback(new Error('提现积分不能超过剩余积分'))
              if (points < 2000) return callback(new Error('最少提现200元'))
              callback()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchUserInfo()
  },
  methods: {
    async fetchUserInfo() {
      const res = await getCurrUserInfo()
      if (res.code === 200 && res.data) {
        this.userInfo = res.data
        if (this.userInfo.url) await this.generateQRCode(this.userInfo.url)
      }
    },
    copyText(text, label) {
      if (!text) return this.$message.warning('内容为空')
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success(`${label}已复制到剪贴板`)
    },
    async refreshPoints() {
      await this.fetchUserInfo()
      this.$message.success('积分信息已刷新')
    },
    handleCloseWithdrawDialog() {
      this.withdrawDialogVisible = false
      this.withdrawForm = { points: '', amount: '0.00' }
    },
    calculateAmount() {
      this.withdrawForm.amount = ((parseInt(this.withdrawForm.points) || 0) / 10).toFixed(2)
    },
    showWithdrawDialog() {
      this.withdrawDialogVisible = true
      this.withdrawForm = { points: '', amount: '0.00' }
    },
    async submitWithdraw() {
      this.$refs.withdrawForm.validate(async (valid) => {
        if (!valid) return
        this.withdrawLoading = true
        try {
          const res = await postAction('/api/user/withdraw', { amount: parseInt(this.withdrawForm.points) })
          if (res.code === 200) {
            this.$message.success('提现申请提交成功，请等待审核')
            this.withdrawDialogVisible = false
            this.withdrawForm = { points: '', amount: '0.00' }
            this.fetchUserInfo()
          } else {
            this.$message.error(res.msg || '提现申请失败')
          }
        } catch {
          this.$message.error('提现失败，请稍后重试')
        } finally {
          this.withdrawLoading = false
        }
      })
    },
    downloadQrCode() {
      if (!this.qrCodeDataUrl) return this.$message.warning('二维码不存在')
      const link = document.createElement('a')
      link.href = this.qrCodeDataUrl
      link.download = `邀请二维码_${this.userInfo.uid || 'qrcode'}_${Date.now()}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$message.success('二维码下载成功')
    },
    async generateQRCode(text) {
      try {
        this.qrCodeDataUrl = await QRCode.toDataURL(text, { width: 180, height: 180, margin: 1, color: { dark: '#000', light: '#FFF' } })
      } catch {
        this.$message.error('生成二维码失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-container {
  padding: 16px;
  max-width: 680px;
  margin: 0 auto;
}

// 个人信息卡片
.profile-card {
  margin-bottom: 16px;
  ::v-deep .el-card__body {
    padding: 0;
  }

  .profile-header {
    display: flex;
    align-items: center;
    padding: 24px;
    gap: 20px;

    .avatar-box {
      flex-shrink: 0;
    }

    .profile-meta {
      flex: 1;
      min-width: 0;

      .profile-name {
        font-size: 20px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 10px;
      }

      .profile-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;

        .meta-tag {
          font-size: 13px;
          color: #606266;

          ::v-deep .el-button {
            padding: 0 4px;
            font-size: 12px;
          }
        }
      }
    }
  }

  .invite-area {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 20px 24px;
    border-top: 1px solid #f0f0f0;
    background: #fafbfc;

    .invite-link-box {
      flex: 1;
      min-width: 0;

      .invite-label {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 8px;
      }

      .invite-link-row {
        display: flex;
        gap: 8px;

        .link-input {
          flex: 1;
        }
      }
    }

    .qrcode-box {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;

      .qrcode-img {
        width: 100px;
        height: 100px;
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: #fff;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .qrcode-loading {
        color: #909399;
        font-size: 12px;
      }
    }
  }
}

// 积分卡片
.points-card {
  ::v-deep .el-card__body {
    padding: 0;
  }

  .points-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;

    h3 {
      font-size: 14px;
      font-weight: 500;
      color: #909399;
      margin: 0;
    }
  }

  .points-grid {
    padding: 28px 24px;
    text-align: center;

    .points-item .points-num {
      font-size: 40px;
      font-weight: 700;
      color: #409eff;
      line-height: 1;
      margin-bottom: 8px;
    }

    .points-desc {
      font-size: 13px;
      color: #909399;
    }
  }
}

// 提现弹窗
.withdraw-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 14px;
    color: #606266;

    &:last-child { margin-bottom: 0; }

    .info-value {
      font-weight: 600;
      &.points { color: #409eff; font-size: 16px; }
    }
  }
}

// 移动端
@media (max-width: 768px) {
  .app-container {
    padding: 10px;
  }

  .profile-card {
    .profile-header {
      padding: 16px;
      gap: 14px;
    }

    .invite-area {
      flex-direction: column;
      padding: 16px;
      gap: 16px;

      .invite-link-box .invite-link-row {
        flex-direction: column;
      }
    }
  }

  .points-card {
    .points-header { padding: 12px 16px; }
    .points-grid { padding: 20px 16px; }
    .points-grid .points-item .points-num {
      font-size: 32px;
    }
  }

  ::v-deep .el-dialog {
    width: 95% !important;
  }
}

@media (max-width: 480px) {
  .profile-card {
    .profile-header {
      flex-direction: column;
      text-align: center;

      .profile-tags {
        justify-content: center;
      }
    }
  }

  .points-card .points-grid .points-item .points-num {
    font-size: 28px;
  }
}
</style>
